import { useState, useEffect } from "react";

import { ThermalModalHeader, ThermalModalFooter } from "../components/common/ThermalDesignerCommonComponents";
import { useThermalDesign } from "../../../ThermalDesignContext";
import { useModalAutoClose } from "../hooks/ThermalDesignerHooks";
import { DividerLine } from "../../../SatelliteProfileImgAssets/SatelliteProfileImgAssets";
import { NodeSpecItem, MaterialsData } from "../../../ThermalDesignTypes";
import { isFieldValid, FIELD_VALIDATION, showInvalidFieldToasts } from "../hooks/ThermalDesignerHooks";

const SingleInputItem = ({ label, fieldName, value, unit, isEnabled = true, handleInputChange, invalidFields, setInvalidFields, required = false }) => {
  const validationFieldName = `node_${fieldName}`;
  const isInvalid = invalidFields.has(validationFieldName);
  const [localStr, setLocalStr] = useState(value !== undefined && value !== null ? String(value) : "");

  useEffect(() => {
    setLocalStr(value !== undefined && value !== null ? String(value) : "");
  }, [value]);

  const getPlaceholder = (): string => {
    if (validationFieldName in FIELD_VALIDATION) {
      const config = FIELD_VALIDATION[validationFieldName];
      return `[${config.min}-${config.max}]`;
    }
    return "0";
  };

  const handleChange = (e) => {
    const filtered = e.target.value.replace(/[^-0-9.]/g, "");
    const dotIndex = filtered.indexOf(".");
    const clamped = dotIndex >= 0 ? filtered.slice(0, dotIndex + 6) : filtered;
    setLocalStr(clamped);
    if (invalidFields.has(validationFieldName)) {
      setInvalidFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(validationFieldName);
        return newSet;
      });
    }
  };

  const handleBlur = () => {
    handleInputChange(fieldName, localStr);
    const numVal = parseFloat(localStr);
    const valid = !isNaN(numVal) && isFieldValid(validationFieldName, numVal);
    setInvalidFields((prev) => {
      const newSet = new Set(prev);
      if (valid) {
        newSet.delete(validationFieldName);
      } else {
        newSet.add(validationFieldName);
        showInvalidFieldToasts(new Set([validationFieldName]));
      }
      return newSet;
    });
  };

  return (
    <>
      <div className="infocard-component-properties-div">
        <div className="infocard-component-properties-sub-div">
          <span className="infocard-units-location-span">
            {label}
            {required && <span className="required-asterisk"> *</span>}
          </span>
          <div className="infocard-units-location-sub-div">
            <div className={`infocard-component-togglebtn-div${isEnabled ? "" : " disabled"}`}>
              <div className="infocard-component-value-div mass-value">
                <input
                  type="text"
                  className={`infocard-location-value ${isInvalid ? "infocard-location-value--invalid" : ""} input-editable`}
                  value={localStr}
                  onChange={handleChange}
                  onFocus={(e) => e.target.select()}
                  onBlur={handleBlur}
                  disabled={!isEnabled}
                  placeholder={getPlaceholder()}
                />
              </div>
              <div className="infocard-component-units-div">
                <span className="infocard-location-y-axis">{unit}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const CustomMaterialModal: React.FC = () => {
  const [isModalMinimized, setIsModalMinimized] = useState(false);
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const { nodeSpecsOptions, nodeSpecsUnits, setNodeSpecsOptions, setOpenModal, isSatDesignerSidebarOpen } = useThermalDesign();
  useModalAutoClose("isCustomMaterialModalOpen");

  const selectedNode = nodeSpecsOptions.find((node) => node.isSelected);

  const [localValues, setLocalValues] = useState({
    specific_heat: selectedNode?.values?.node_material_properties?.specific_heat ?? 0,
    absorptivity: selectedNode?.values?.node_material_properties?.absorptivity ?? 0,
    emissivity: selectedNode?.values?.node_material_properties?.emissivity ?? 0,
  });

  // Sync localValues when selected node changes
  useEffect(() => {
    if (selectedNode?.values?.node_material_properties) {
      setLocalValues({
        specific_heat: selectedNode.values.node_material_properties.specific_heat ?? 0,
        absorptivity: selectedNode.values.node_material_properties.absorptivity ?? 0,
        emissivity: selectedNode.values.node_material_properties.emissivity ?? 0,
      });
    }
  }, [selectedNode?.id, selectedNode?.values?.node_material_properties]);

  const header = { title: "Custom", isModalMinimized, setIsModalMinimized, setOpenModal, modalItem: "isCustomMaterialModalOpen" };

  const handleCancel = () => {
    setOpenModal((prev) => ({
      ...prev,
      isCustomMaterialModalOpen: false,
    }));
  };

  const handleInputChange = (fieldName: string, newValue: string) => {
    const numValue = parseFloat(newValue) || 0;
    setLocalValues((prev) => ({
      ...prev,
      [fieldName]: numValue,
    }));

    // Clear field from invalidFields when user starts editing
    const validationFieldName = `node_${fieldName}`;
    if (invalidFields.has(validationFieldName)) {
      setInvalidFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(validationFieldName);
        return newSet;
      });
    }
  };

  const validateCustomMaterialFields = (): Set<string> => {
    const fields: Array<[string, number]> = [
      ["node_specific_heat", localValues.specific_heat],
      ["node_absorptivity", localValues.absorptivity],
      ["node_emissivity", localValues.emissivity],
    ];
    return new Set(fields.filter(([k, v]) => !isFieldValid(k, v)).map(([k]) => k));
  };

  const handleSave = () => {
    const invalid = validateCustomMaterialFields();
    setInvalidFields(invalid);

    if (invalid.size > 0) {
      showInvalidFieldToasts(invalid);
      return;
    }

    if (selectedNode?.values?.node_material_properties) {
      const updatedOptions: typeof nodeSpecsOptions = nodeSpecsOptions.map((node) => {
        if (node.id === selectedNode.id) {
          const currentProps = node.values?.node_material_properties as MaterialsData || {};
          const updatedNode = {
            ...node,
            values: {
              ...node.values,
              node_specific_heat: localValues.specific_heat,
              node_absorptivity: localValues.absorptivity,
              node_emissivity: localValues.emissivity,
              node_material_properties: {
                material_name: currentProps.material_name || "Custom",
                specific_heat: localValues.specific_heat,
                thermal_conductivity: currentProps.thermal_conductivity ?? 0,
                density: currentProps.density ?? 0,
                absorptivity: localValues.absorptivity,
                emissivity: localValues.emissivity,
                youngs_modulus: currentProps.youngs_modulus ?? 0,
                yield_strength: currentProps.yield_strength ?? 0,
                cte: currentProps.cte ?? 0,
              } as MaterialsData,
            },
          } as NodeSpecItem;
          return updatedNode;
        }
        return node;
      });
      setNodeSpecsOptions(updatedOptions);
    }

    setOpenModal((prev) => ({
      ...prev,
      isCustomMaterialModalOpen: false,
    }));
  };

  return (
    <div className={`nodeSpecsModal customMaterialsModal ${!isSatDesignerSidebarOpen ? "left" : "right"}`}>
      <ThermalModalHeader header={header} />
      {!isModalMinimized && (
        <>
          <div className="nodeSpecsModal__inputs">
            <SingleInputItem
              label="Specific Heat"
              fieldName="specific_heat"
              value={localValues.specific_heat}
              unit={nodeSpecsUnits?.node_material_properties?.specific_heat}
              handleInputChange={handleInputChange}
              invalidFields={invalidFields}
              setInvalidFields={setInvalidFields}
              required
            />
            <SingleInputItem
              label="Absorptivity"
              fieldName="absorptivity"
              value={localValues.absorptivity}
              unit={nodeSpecsUnits?.node_material_properties?.absorptivity}
              handleInputChange={handleInputChange}
              invalidFields={invalidFields}
              setInvalidFields={setInvalidFields}
              required
            />
            <SingleInputItem
              label="Emissivity"
              fieldName="emissivity"
              value={localValues.emissivity}
              unit={nodeSpecsUnits?.node_material_properties?.emissivity}
              handleInputChange={handleInputChange}
              invalidFields={invalidFields}
              setInvalidFields={setInvalidFields}
              required
            />
          </div>
          <div className="nodeSpecsModal__divider__div">
            <DividerLine />
          </div>
          <ThermalModalFooter onSave={handleSave} isLastNode={true} onCancel={handleCancel} saveLabel="Save & Exit" />
        </>
      )}
    </div>
  );
};

export default CustomMaterialModal;
